import { useState } from "react";
import { Navbar } from "../components/Navbar";
import Button from "../utils/Button";
import { Check, Eye, EyeOff, X } from "lucide-react";
import { Modal } from "../utils/Modal";
import { useNavigate } from "react-router-dom";
import apiCall from "../network/api";
import { setAuthItem } from "../utils/indexDB";

export const ObcLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setError(null);
    setEmail(newEmail);
    if (newEmail.length > 0) {
      setIsValidEmail(validateEmail(newEmail));
    } else {
      setIsValidEmail(null);
    }
  };

  const setEmailAndToken = async (token, email, userID) => {
    await setAuthItem("accessToken", token);
    await setAuthItem("userEmail", email);
    await setAuthItem("userId", userID);
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const data = await apiCall({
        endpoint: "/login",
        method: "POST",
        data: { email: email?.trim(), password: password?.trim() },
      });

      if (data?.success) {
        await setEmailAndToken(data.token, email, data.data);
        navigate("/home", { state: { email: email, token: data.token } });
      } else {
        throw new Error(data?.message || "Login failed");
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setIsModalOpen(true);
  };

  return (
    <div className="h-screen flex flex-col justify-between">
      <div>
        <Navbar />
        <div className="max-w-sm mx-auto p-6 bg-white rounded-lg">
          <h2 className="text-2xl font-semibold mb-6 text-gray-500">
            Nice to see you again
          </h2>
          {error && (
            <div className="bg-customError-100 ring-1 ring-customError-200 rounded-md p-3 mb-4">
              <p className="text-sm font-semibold text-customError-500">
                {error?.data?.message}
              </p>
            </div>
          )}

          <form className="space-y-4">
            <div>
              <div className="relative">
                <input
                  value={email}
                  onChange={handleEmailChange}
                  type="email"
                  placeholder="Enter your email"
                  className={`w-full p-3 bg-gray-100 text-gray-500 ring-1 ${
                    error ? "ring-customError-500" : "ring-gray-300"
                  } bg-gray-100 rounded-md focus:outline-none focus:ring-1 ${
                    error
                      ? "focus:ring-customError-500"
                      : isValidEmail === true
                        ? "focus:ring-primary-500"
                        : isValidEmail === false
                          ? "focus:ring-customError-500"
                          : "focus:ring-primary-500"
                  } pr-10`}
                />
                {isValidEmail !== null && (
                  <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    {isValidEmail ? (
                      <Check className="h-5 w-5 text-primary-500" />
                    ) : (
                      <X className="h-5 w-5 text-customError-500" />
                    )}
                  </span>
                )}
              </div>
            </div>

            <div className="relative">
              <input
                value={password}
                onChange={(e) => {
                  setError(null);
                  setPassword(e.target.value);
                }}
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className={`w-full p-3 text-gray-500 ring-1 ${
                  error ? "ring-customError-500" : "ring-gray-300"
                } bg-gray-100 rounded-md focus:outline-none focus:ring-1 ${
                  error
                    ? "focus:ring-customError-500"
                    : "focus:ring-primary-500"
                }  pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>

            <div className="text-right text-xs font-semibold text-gray-400">
              <button onClick={handleOpen} type="button">
                Trouble logging in?
              </button>

              <Modal
                isOpen={isModalOpen}
                header={"Login Assistance"}
                message={
                  <>
                    If you're experiencing any issues logging in, please contact
                    our Operations Manager at
                    <span
                      style={{
                        margin: "0rem 0.2rem",
                        color: "blue",
                        textDecoration: "underline",
                      }}
                    >
                      <a href="mailto:obc@scienslogistics.com">
                        obc@scienslogistics.com
                      </a>
                    </span>
                    . They will assist you in resolving any account access
                    problems.
                  </>
                }
                buttonTitle={"Close"}
                buttonAction={() => setIsModalOpen(false)}
              />
            </div>

            <Button
              text={"Login"}
              size={"md"}
              disabled={!isValidEmail || password?.length === 0}
              onClick={handleLogin}
              loading={loading}
            />
            <p className="text-sm w-full flex justify-center gap-2">
              <span className="text-gray-400">
                Want to work as OBC partner?{" "}
              </span>
              <span
                className="text-primary-500 underline cursor-pointer"
                onClick={() => navigate("/")}
              >
                Sign up
              </span>
            </p>
            <Modal
              isOpen={loading}
              isLoading={loading}
              loadingMessage={
                "Establishing secure connection and verifying credentials."
              }
            />
          </form>
        </div>
      </div>
      <div className="text-center text-xs text-gray-300">
        <p>© Sciens Logistics LLC</p>
      </div>
    </div>
  );
};
